import { readString, STORAGE_KEYS, writeString } from './storage';

const BASE = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const getPlayerToken = (): string | null => readString(STORAGE_KEYS.player);
export const setPlayerToken = (token: string): void => writeString(STORAGE_KEYS.player, token);

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
}

/** JSON fetch against /api with the player identity header (cookie fallback, PRD §7). */
export async function api<T>(
  path: string,
  { method = 'GET', body }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const token = getPlayerToken();
  if (token) headers['X-Player'] = token;

  let res: Response;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(
      'You appear to be offline. Check your connection and try again.',
      'offline',
      0,
    );
  }

  if (!res.ok) {
    let code = 'http_error';
    let message = `Request failed (${res.status})`;
    try {
      const j = (await res.json()) as { code?: string; error?: string };
      if (j.code) code = j.code;
      if (j.error) message = j.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, code, res.status);
  }
  return (await res.json()) as T;
}
